import React, { useCallback, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { FormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import CancelButton from '../../../../../components/form/CancelButton';
import { Input } from '../../../../../components/form/Input';
import SubmitButton from '../../../../../components/form/SubmitButton';
import { ModalFooter } from '../../../../../components/Modal';
import {
  TimeEntryNotesFormData,
  useTimeEntryNotesForm,
} from '../../../hooks/useTimeEntryNotesForm';
import { ExtraFieldsAreaSimple } from '../../../components/ExtraFields/ExtraFieldsArea/ExtraFieldsAreaSimple';
import {
  ExtraFieldInternalValue,
  mapToExtraFieldInput,
} from '../../../components/ExtraFields/extraFieldInternalValue';
import {
  ExtraFieldsSupportedObject,
  ExtraFieldsFormId,
  ExtraFieldContextParam,
} from '../../../components/ExtraFields/types';
import { ExtraFieldInput } from '../../../__generated__/graphql';
import { useClockOutRequirements } from '../../../hooks/timeTracking/useClockOutRequirements';

type Props = {
  timeEntryId: string;
  notes: string | null;
  show: boolean;
  onClose: () => void;
  onSubmit: (
    notes: string | null,
    extraFields: ExtraFieldInput[],
  ) => Promise<void>;
  extraFieldValues: readonly ExtraFieldInternalValue[];
  recordTypeName: string | null;
  contextParams: ExtraFieldContextParam[];
};

const StopTrackingModal: React.FC<Props> = ({
  show,
  onClose,
  onSubmit,
  notes,
  extraFieldValues,
  recordTypeName,
  contextParams,
}) => {
  const { t } = useTranslation();

  const { requireNotesOnClockOut } = useClockOutRequirements();
  const { form, fieldName } = useTimeEntryNotesForm({
    notes: notes,
    required: requireNotesOnClockOut,
  });

  const [extraFields, setExtraFields] =
    useState<readonly ExtraFieldInternalValue[]>(extraFieldValues);

  const handleExtraFieldChange = useCallback(
    (values: ExtraFieldInternalValue[]) => {
      setExtraFields(values);
    },
    [],
  );

  const handleSubmit = useCallback(
    (form: TimeEntryNotesFormData) =>
      onSubmit(form.notes, extraFields.map(mapToExtraFieldInput)),
    [onSubmit, extraFields],
  );

  return (
    <Modal show={show} onHide={onClose} centered>
      <FormContext {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)}>
          <Modal.Header closeButton className="card-header">
            <h4 className="card-header-title">
              {t('timeTracking.current.stopTrackingModal.title')}
            </h4>
          </Modal.Header>
          <Modal.Body>
            <Input
              name={fieldName}
              type="textarea"
              label={t('timeTracking.current.stopTrackingModal.content')}
              placeholder={t(
                'timeTracking.current.stopTrackingModal.notesPlaceholder',
              )}
              minRows={4}
            />
            <div className="mt-3">
              <ExtraFieldsAreaSimple
                objectApiName={ExtraFieldsSupportedObject.TimeEntry}
                formId={ExtraFieldsFormId.TimeEntryEdit}
                onChange={handleExtraFieldChange}
                values={extraFields}
                recordTypeName={recordTypeName}
                contextParams={contextParams}
              />
            </div>
          </Modal.Body>
          <ModalFooter>
            <CancelButton size="sm" onClick={onClose}>
              {t('timeTracking.current.stopTrackingModal.cancelButton')}
            </CancelButton>

            <SubmitButton size="sm">
              {t('timeTracking.current.stopTrackingModal.confirmButton')}
            </SubmitButton>
          </ModalFooter>
        </form>
      </FormContext>
    </Modal>
  );
};

export default StopTrackingModal;
